/**
 * BrandLogo — the real iTow wordmark.
 *
 * Two artwork files ship in `public/brand/`, both transparent PNGs:
 *   - `itow-logo-navy.png`  — navy + red type, for LIGHT surfaces.
 *   - `itow-logo-white.png` — white + red type, for DARK / navy surfaces.
 *
 * Which file to use depends on the surface the logo sits ON, not on the app's
 * color mode: the navy sidebar rail is dark even in light mode, so it always
 * takes the white mark. Callers state that with the `on` prop:
 *
 *   <BrandLogo on="dark" height={26} />   // navy rail, login hero
 *   <BrandLogo on="light" height={36} />  // white masthead
 *   <BrandLogo height={36} />             // follows the active color mode
 *
 * This replaces the generic LocalShipping truck placeholder in every brand
 * position. Functional wayfinding icons (map, chat, users…) stay as they are.
 */
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useColorMode } from '../../theme/theme';

const LOGO_NAVY = '/brand/itow-logo-navy.png';
const LOGO_WHITE = '/brand/itow-logo-white.png';

export interface BrandLogoProps {
  /**
   * The surface the logo is placed on. `'auto'` (default) follows the active
   * color mode; `'dark'` forces the white mark (navy rail, login hero).
   */
  on?: 'light' | 'dark' | 'auto';
  /** Rendered height in px. Width scales automatically. Defaults to 32. */
  height?: number;
  /** Extra sx merged onto the <img>. */
  sx?: SxProps<Theme>;
  /**
   * When true the logo is purely decorative because a visible text label sits
   * next to it — renders empty alt text so screen readers don't say it twice.
   */
  decorative?: boolean;
}

export default function BrandLogo({
  on = 'auto',
  height = 32,
  sx,
  decorative = false,
}: BrandLogoProps) {
  const { t } = useTranslation();
  const { mode } = useColorMode();

  const surface = on === 'auto' ? mode : on;
  const src = surface === 'dark' ? LOGO_WHITE : LOGO_NAVY;

  return (
    <Box
      component="img"
      src={src}
      alt={decorative ? '' : t('app.brand')}
      // Decorative instances are already described by the adjacent text label.
      aria-hidden={decorative || undefined}
      sx={{ height, width: 'auto', display: 'block', ...sx }}
    />
  );
}
